/**
 * Dynamic Lucide icon resolver.
 *
 * Maps icon name strings (from the navigation API) to Lucide React
 * components.  Accepts both PascalCase ("LayoutDashboard") and
 * kebab-case ("layout-dashboard") inputs — the latter is converted
 * to PascalCase before lookup.
 *
 * Uses the `icons` barrel from lucide-react which re-exports every
 * icon keyed by PascalCase name, avoiding manual enumeration.
 *
 * Cross-References:
 *   - S-MOD.11: Frontend dynamic navigation
 *   - FR-UI-001: Dynamic nav item registration
 */
import { icons, type LucideIcon } from 'lucide-react';

/** Convert kebab-case to PascalCase: "layout-dashboard" → "LayoutDashboard" */
function kebabToPascal(name: string): string {
  return name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

/**
 * Resolve a Lucide icon by name.
 *
 * @param name Icon name in PascalCase or kebab-case.
 * @returns The matching LucideIcon component, or a fallback circle icon.
 */
export function resolveIcon(name: string | null | undefined): LucideIcon {
  if (!name) return icons.Circle;

  // Try as-is first (PascalCase from manifests)
  const direct = icons[name as keyof typeof icons];
  if (direct) return direct;

  // Try kebab-case → PascalCase conversion (seed data format)
  const pascal = kebabToPascal(name);
  const converted = icons[pascal as keyof typeof icons];
  if (converted) return converted;

  // Fallback
  return icons.Circle;
}
